import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { Flex } from '@blockanalitica/ui';
import type { SimpleChartDataset } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  CurrencyValue,
  formatCurrency,
  BarChart,
  formatDatetime,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs({
  ilk,
  urn
}: {
  ilk: string;
  urn: string;
}) {
  const [activeDisplayOption, setActiveDisplayOption] =
    useState('deposit_withdraw');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const displayOptions = useMemo(
    () => [
      { key: 'deposit_withdraw', value: 'Deposit/Withdraw' },
      { key: 'borrow_repay', value: 'Borrow/Repay' }
    ],
    []
  );

  const [data, error] = useApi({
    url: `/groups/core/ilks/${ilk}/cdps/${urn}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const theme = useTheme();

  const datasets = useMemo(() => {
    let datasets: SimpleChartDataset[] = [];
    if (activeDisplayOption === 'deposit_withdraw') {
      datasets = [
        {
          xField: 'date',
          yField: 'deposit',
          label: 'Deposit',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'withdraw',
          label: 'Withdraw',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else if (activeDisplayOption === 'borrow_repay') {
      datasets = [
        {
          xField: 'date',
          yField: 'borrow',
          label: 'Borrow',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'repay',
          label: 'Repay',
          backgroundColor: theme.colors.chartDown
        }
      ];
    }
    return datasets;
  }, [activeDisplayOption, theme.colors]);

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const yScaleValueFormatter = useCallback((value: number) => {
    return formatCurrency(value);
  }, []);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const valueFormatter = useCallback((value: number) => {
    return <CurrencyValue value={value} options={{ currency: 'USD' }} />;
  }, []);

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <BarChart
          key={activeDisplayOption}
          data={data.volume}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          subValueFormatter={subValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
